import {
    type TextStyle,
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    CalendarCellTitleVariantConstantType,
    CalendarCellColorsConstantType,
    CalendarCellFilledConstantType,
    CalendarCellLabelConstantType,
    CalendarCellTitleConstantType,
    CalendarCellDynamicStyleType,
    CalendarCellRangePosition,
    CalendarCellColors
} from "./type";
import type {
    Mutable
} from "../../types";
import type {
    DateCalendarType
} from "../dateTimePicker/type";
import {
    type ElevationLevelType,
    getElevationStyle
} from "../../helpers/theme/elevation";
import {
    getFocusRingStyle
} from "../../helpers/theme/state";
import {
    formatDateTime
} from "../../helpers/locale/format";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getMotionTransition
} from "../../helpers/motion";
import {
    webStyle
} from "../../utils";

export const CALENDAR_CELL_TITLE_FORMATS: Record<DateCalendarType, Intl.DateTimeFormatOptions> = {
    year: {
        year: "numeric"
    },
    day: {
        day: "numeric"
    },
    month: {
        month: "short"
    }
};

export const CALENDAR_CELL_LABEL_FORMATS: Record<DateCalendarType, Intl.DateTimeFormatOptions> = {
    day: {
        weekday: "long",
        year: "numeric",
        day: "numeric",
        month: "long"
    },
    month: {
        year: "numeric",
        month: "long"
    },
    year: {
        year: "numeric"
    }
};

export const CALENDAR_CELL_TODAY_KEYS: Record<DateCalendarType, keyof NCoreUIKit.Translation> = {
    month: "calendar-cell-this-month",
    year: "calendar-cell-this-year",
    day: "calendar-cell-today"
};

export const CALENDAR_CELL_RANGE_KEYS: Partial<Record<CalendarCellRangePosition, keyof NCoreUIKit.Translation>> = {
    start: "calendar-cell-range-start",
    end: "calendar-cell-range-end"
};

export const CALENDAR_CELL_FILLED_ELEVATION: ElevationLevelType = "level1";

export const getCalendarCellIsFilled = ({
    rangePosition,
    isSelected
}: CalendarCellFilledConstantType): boolean => {
    return isSelected || rangePosition === "start" || rangePosition === "end";
};

export const getCalendarCellTitleVariant = ({
    isFilled,
    isToday
}: CalendarCellTitleVariantConstantType): keyof NCoreUIKit.Typography => {
    return isFilled || isToday ? "labelMediumSize" : "bodyMediumSize";
};

export const getCalendarCellColors = ({
    rangePosition,
    isDisabled,
    isOutside,
    isFilled,
    isToday,
    colors
}: CalendarCellColorsConstantType): CalendarCellColors => {
    const currentColors: CalendarCellColors = {
        ringColor: isToday && !isFilled ? isDisabled ? colors.content.border.disabled : colors.content.icon.low : "transparent",
        titleColor: isOutside && !rangePosition ? colors.content.text.low : colors.content.text.high,
        bandColor: rangePosition ? colors.content.container.selected : "transparent",
        surfaceColor: "transparent"
    };

    if(isFilled) {
        currentColors.surfaceColor = isDisabled ? colors.system.state.overlay.disabled.neutral : colors.content.container.primary;

        currentColors.titleColor = isDisabled ? colors.system.state.content.disabled.neutral : colors.content.text.onPrimary;

        return currentColors;
    }

    if(isDisabled) {
        currentColors.titleColor = colors.content.text.disabled;
    }

    return currentColors;
};

export const getCalendarCellTitle = ({
    activeLocale,
    calendarType,
    date
}: CalendarCellTitleConstantType): string => {
    return formatDateTime({
        options: CALENDAR_CELL_TITLE_FORMATS[calendarType],
        locale: activeLocale,
        date
    });
};

export const getCalendarCellLabel = ({
    rangePosition,
    activeLocale,
    calendarType,
    isDisabled,
    localize,
    isToday,
    date
}: CalendarCellLabelConstantType): string => {
    let label = formatDateTime({
        options: CALENDAR_CELL_LABEL_FORMATS[calendarType],
        locale: activeLocale,
        date
    });

    if(isToday) {
        label = localize(CALENDAR_CELL_TODAY_KEYS[calendarType], [
            label
        ]);
    }

    const rangeKey = rangePosition ? CALENDAR_CELL_RANGE_KEYS[rangePosition] : undefined;

    if(rangeKey) {
        label = localize(rangeKey, [
            label
        ]);
    }

    if(isDisabled) {
        label = localize("calendar-cell-unavailable", [
            label
        ]);
    }

    return label;
};

const stylesheet = StyleSheet.create({
    container: {
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    surface: {
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "stretch",
        flexGrow: 1
    },
    title: {
        textAlign: "center",
        fontVariant: [
            "tabular-nums"
        ]
    },
    stretchedContainer: {
        flexBasis: 0,
        flexGrow: 1
    }
});

export const useStyles = ({
    isReduceMotionEnabled,
    spreadBehaviour,
    controlHeights,
    isFocusVisible,
    currentColors,
    rangePosition,
    shapeConfigs,
    shadowAlpha,
    elevations,
    isDisabled,
    durations,
    isFilled,
    radiuses,
    borders,
    colors
}: CalendarCellDynamicStyleType) => {
    const {
        borderRadius: scaledRadius,
        ...cornerShape
    } = getCornerStyle({
        radius: radiuses.actions,
        shapeConfigs
    });

    const styles = {
        surface: {
            backgroundColor: currentColors.surfaceColor,
            borderColor: currentColors.ringColor,
            borderRadius: scaledRadius,
            ...cornerShape,
            borderWidth: borders.selection
        } as Mutable<ViewStyle>,
        container: {
            backgroundColor: currentColors.bandColor,
            height: controlHeights.medium,
            width: controlHeights.medium
        } as Mutable<ViewStyle>,
        title: {
        } as Mutable<TextStyle>
    };

    if(spreadBehaviour === "stretch") {
        delete styles.container.width;

        Object.assign(styles.container, stylesheet.stretchedContainer);
    }

    if(rangePosition) {
        Object.assign(styles.container, cornerShape);
    }

    if(rangePosition === "start") {
        styles.container.borderBottomStartRadius = scaledRadius;

        styles.container.borderTopStartRadius = scaledRadius;
    }

    if(rangePosition === "end") {
        styles.container.borderBottomEndRadius = scaledRadius;

        styles.container.borderTopEndRadius = scaledRadius;
    }

    if(isFilled && !isDisabled && elevations[CALENDAR_CELL_FILLED_ELEVATION].length) {
        Object.assign(styles.surface, getElevationStyle({
            level: CALENDAR_CELL_FILLED_ELEVATION,
            shadowColor: colors.system.shadow,
            shadowAlpha,
            elevations
        }));
    }

    Object.assign(styles.surface, getFocusRingStyle({
        isFocusVisible,
        borders,
        colors
    }));

    if(isFocusVisible) {
        Object.assign(styles.container, {
            zIndex: shapeConfigs.layers.raised
        });
    }

    Object.assign(styles.surface, webStyle(getMotionTransition({
        properties: [
            "background-color",
            "border-color",
            "box-shadow"
        ],
        isReduceMotionEnabled,
        intent: "hover",
        durations
    })));

    Object.assign(styles.container, webStyle(getMotionTransition({
        isReduceMotionEnabled,
        properties: [
            "background-color"
        ],
        intent: "hover",
        durations
    })));

    Object.assign(styles.title, webStyle(getMotionTransition({
        isReduceMotionEnabled,
        intent: "hover",
        properties: [
            "color"
        ],
        durations
    })));

    return styles;
};
export default stylesheet;
